import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { patientConditionInputSchema, type PatientConditionInput } from "@dosa/validation";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  EmptyState,
  FieldError,
  FieldLabel,
  Input,
  PageLoading,
} from "@dosa/ui";
import { useAuth } from "../../hooks/useAuth";
import { addPatientCondition, listPatientConditions } from "../../lib/api-client";

const statusVariant = { ACTIVE: "warning", RESOLVED: "success", CHRONIC_MANAGED: "info", IN_REMISSION: "neutral" } as const;

export function PatientConditionsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const patientId = user!.patientId!;
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data: conditions, isLoading } = useQuery({
    queryKey: ["patient-conditions", patientId],
    queryFn: () => listPatientConditions(patientId),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PatientConditionInput>({ resolver: zodResolver(patientConditionInputSchema), defaultValues: { status: "ACTIVE" } });

  const mutation = useMutation({
    mutationFn: (input: PatientConditionInput) => addPatientCondition(patientId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-conditions", patientId] });
      reset();
      setOpen(false);
    },
  });

  if (isLoading) return <PageLoading />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-charcoal-900">{t("patient.conditions.title")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger render={<Button />}>
            <Plus className="h-4 w-4" /> {t("patient.conditions.addCondition")}
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("patient.conditions.addCondition")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4" noValidate>
              <div>
                <FieldLabel htmlFor="freeTextName">{t("patient.conditions.title")}</FieldLabel>
                <Input id="freeTextName" placeholder="e.g. Hypertension" {...register("freeTextName")} />
                <FieldError>{errors.freeTextName?.message}</FieldError>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  {t("common.cancel")}
                </Button>
                <Button type="submit" isLoading={isSubmitting}>
                  {t("common.save")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {!conditions || conditions.length === 0 ? (
            <EmptyState className="border-0" title={t("patient.conditions.empty")} />
          ) : (
            <ul className="divide-y divide-charcoal-100">
              {conditions.map((c) => (
                <li key={c.id} className="flex items-center justify-between px-5 py-4">
                  <p className="font-medium text-charcoal-800">{c.freeTextName}</p>
                  <Badge variant={statusVariant[c.status]}>{c.status.replaceAll("_", " ")}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
